/**
 * PreMiD — Discord Rich Presence for websites
 *
 * Its first web-accessible-resource entry — `variableGetterDependencies.js`,
 * `presences/*`, `content-scripts/notification/*` and `icons/PreMiD.png` — is matched
 * to every origin with no `use_dynamic_url`, so those files are fetchable from any page
 * at a fixed URL. A second entry hides only `content-scripts/notification.css` behind
 * `use_dynamic_url: true`, which we probe as a control.
 *
 * The vendor logo is the cleanest signal: `icons/PreMiD.png` is uniquely named and
 * loads through `probeImage` with no CORS grant, surviving a restrictive page CSP.
 *
 * Manifest verified against version 2.13.1.
 */

import { probeImage, probeFetch } from '../core/probe';
import type { ExtensionDefinition } from '../core/types';

const ID = 'agjnjboanicjcpenljmaaigopkgdnihi';

export const preMid: ExtensionDefinition = {
  id: ID,
  name: 'PreMiD',
  storeUrl: `https://chromewebstore.google.com/detail/${ID}`,
  users: 400_000,
  notes:
    'Exposes its icon, presences and getter script to every origin with no dynamic URL; ' +
    'only the notification CSS uses use_dynamic_url, probed here as a control. The ' +
    'vendor-named PreMiD.png is conclusive and needs no CORS grant.',
  signals: [
    {
      id: 'war-premid-icon',
      kind: 'web-accessible-resource',
      description:
        'Load icons/PreMiD.png via <img> — the uniquely vendor-named logo exposed to ' +
        'every origin; needs no CORS grant, so it works even under a strict page CSP.',
      weight: 1,
      run: () => probeImage(ID, 'icons/PreMiD.png'),
    },
    {
      id: 'war-variable-getter-js',
      kind: 'web-accessible-resource',
      description:
        'Fetch variableGetterDependencies.js — a named injection script exposed to ' +
        'every origin, an independent non-image path.',
      weight: 0.5,
      run: () => probeFetch(ID, 'variableGetterDependencies.js'),
    },
    {
      id: 'war-netflix-presence-js',
      kind: 'web-accessible-resource',
      description:
        'Fetch presences/Netflix/presence.js — one of the bundled per-site presence ' +
        'scripts, a distinctive third path.',
      weight: 0.5,
      run: () => probeFetch(ID, 'presences/Netflix/presence.js'),
    },
    {
      id: 'control-dynamic-notification-css',
      kind: 'web-accessible-resource',
      description:
        'CONTROL: fetch content-scripts/notification.css, declared use_dynamic_url. It ' +
        'should fail even when installed — the URL rotates per session — demonstrating ' +
        'the mitigation PreMiD applied to that one resource but not the others.',
      weight: 0,
      control: true,
      run: () => probeFetch(ID, 'content-scripts/notification.css'),
    },
  ],
};
